/**
 * CR043: READ Phase Types and Error Taxonomy
 */

// READ Status enum values
export type ReadStatus = 
  | 'READY_FOR_SCAN' 
  | 'RETRYING' 
  | 'PARTIAL_CONTENT' 
  | 'UNREADABLE_SOURCE' 
  | 'SKIPPED'
  | 'pending';

// Quality Flags (observations during extraction)
export const READ_QUALITY_FLAGS = {
  COOKIE_MODAL_PRESENT: 'Cookie/consent overlay detected',
  COOKIE_TEXT_DOMINANT: 'Cookie text dominated extracted content',
  LOCATION_GATE_PRESENT: 'Location/postal gate detected',
  LOCATION_NOT_SET: 'Location was required but not successfully set',
  NAV_HEADER_ONLY: 'Mostly navigation/header/footer content',
  LOW_TEXT_LENGTH: 'Not enough text extracted',
  JS_CONTENT_NOT_RENDERED: 'Page content did not render in time',
  IMAGE_HEAVY_PAGE: 'Page appears to be mostly images',
  PDF_DETECTED: 'PDF source detected',
  PDF_TEXT_EMPTY: 'PDF has no selectable text layer',
  OCR_USED: 'OCR was required to extract text',
  CAPTCHA_OR_BOT_BLOCK: 'Bot protection/CAPTCHA detected',
  NETWORK_TIMEOUT: 'Page load timed out',
  CONTENT_OK: 'Usable content extracted',
} as const;

export type ReadQualityFlag = keyof typeof READ_QUALITY_FLAGS;

// Final Failure Reasons (one primary reason)
export const READ_FAILURE_REASONS = {
  FAILED_COOKIE_BLOCKING: 'Blocked by cookie/consent overlay (could not dismiss).',
  FAILED_LOCATION_GATE_BLOCKING: 'Blocked by location/store selection (could not set postal code).',
  FAILED_BOT_PROTECTION: 'Blocked by bot protection/CAPTCHA.',
  FAILED_NO_RENDERED_CONTENT: 'Content did not render (page returned mostly empty/chrome).',
  FAILED_LOW_QUALITY_CONTENT: 'Only boilerplate content extracted (header/nav/cookie text).',
  FAILED_TIMEOUT: 'Timed out while loading or extracting content.',
  FAILED_PDF_TEXT_EMPTY_OCR_DISABLED: 'PDF had no text layer and OCR fallback is disabled.',
  FAILED_UNKNOWN: 'Failed to extract usable content (unknown).',
} as const;

export type ReadFailureReason = keyof typeof READ_FAILURE_REASONS;

// Status display configuration
export const READ_STATUS_CONFIG: Record<ReadStatus, {
  label: string;
  icon: 'check' | 'refresh' | 'alert' | 'x' | 'skip';
  color: string;
  tooltip: string;
}> = {
  READY_FOR_SCAN: {
    label: 'Ready',
    icon: 'check',
    color: 'bg-green-500',
    tooltip: 'Usable content extracted (passed quality threshold).',
  },
  RETRYING: {
    label: 'Retrying',
    icon: 'refresh',
    color: 'bg-blue-500',
    tooltip: 'Content quality was too low (e.g., cookie modal or navigation text). Retrying…',
  },
  PARTIAL_CONTENT: {
    label: 'Partial',
    icon: 'alert',
    color: 'bg-yellow-500',
    tooltip: 'Some usable content found, but incomplete. Scan may miss deals.',
  },
  UNREADABLE_SOURCE: {
    label: 'Unreadable',
    icon: 'x',
    color: 'bg-red-500',
    tooltip: 'Could not extract usable content after all retries.',
  },
  SKIPPED: {
    label: 'Skipped',
    icon: 'skip',
    color: 'bg-gray-400',
    tooltip: 'Skipped due to configuration or missing inputs.',
  },
  pending: {
    label: 'Pending',
    icon: 'skip',
    color: 'bg-gray-300',
    tooltip: 'Not yet processed.',
  },
};

// READ attempt log entry structure
export interface ReadAttemptLogEntry {
  attempt_num: number;
  actions: string[];
  quality_score: number;
  flags: string[];
  text_length: number;
  excerpt: string;
  next_step: 'Retrying…' | 'Accepted' | 'Failed';
  timestamp?: string;
}

// Merchant READ data interface
export interface PocRunMerchantRead {
  id: string;
  poc_run_id: string;
  merchant_id: string;
  merchant_name: string | null;
  source_url: string | null;
  status: string;
  read_status: ReadStatus;
  read_attempts_count: number;
  read_quality_score: number | null;
  read_quality_flags: string[] | null;
  read_failure_reason: string | null;
  read_failure_screenshot_url: string | null;
  read_attempts_log: ReadAttemptLogEntry[] | null;
  text_excerpt: string | null;
  text_char_count: number | null;
  text_source_type: string | null;
}

// Run READ summary interface
export interface PocRunReadSummary {
  read_attempts_avg: number | null;
  merchants_readable: number;
  merchants_blocked: number;
  top_failure_reasons: Array<{ reason: string; count: number }>;
}
